// frontend/app/components/lessonPlans/present/presentAskClient.js
//
// One question, one answer, on a wall.
//
// WHY PRESENT MODE ASKS FOR ITSELF. The app already has an ask — AITutorApp's
// handleAsk — and it does far more than fetch an answer: it appends a turn to
// the Saras rail's conversation history, drives text-to-speech, re-scores the
// Curiosity Sparks list and records latency metrics against the student's
// session. Every one of those is right beside a laptop and wrong on a
// projector. The rail is not mounted in present mode, so its answer would land
// nowhere; and a teacher asking the room's question must not rewrite the
// conversation history of whoever is signed in on that machine.
//
// So this is a second CALLER, not a second client. `streamNdjson` is the same
// reader handleAsk uses — same Authorization and X-Content-DB defaults, same
// NDJSON contract — and the body below is handleAsk's body with the fields a
// classroom has no use for turned off. /api/ask_ai stays the single
// orchestration point it already was.
//
// ASKING IS NOT RECORDING. This module can reach exactly one endpoint, and
// /api/ask_ai answers a question. It is not a lesson response, not an
// evaluation and not a mastery write. There is no student here to attribute an
// answer to, and nothing on this path could store one if there were.

import AsyncStorage from "@react-native-async-storage/async-storage";
import axios from "axios";

import { FASTAPI_BASE_URL } from "../../../config";
import { streamNdjson } from "../../../utils/streamingFetch";

const ASK_URL = `${FASTAPI_BASE_URL}/ask_ai`;

/**
 * A question typed at the front of a class, not an essay. The cap is generous
 * enough for anything a teacher would read out and small enough that a stray
 * paste cannot become the prompt.
 */
export const PRESENT_ASK_MAX_CHARS = 400;

/** Thrown for the two things the caller can tell the teacher about precisely. */
export const ASK_EMPTY = "ASK_EMPTY";
export const ASK_TOO_LONG = "ASK_TOO_LONG";

const HTML_ENTITIES = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'",
  "&nbsp;": " ",
};

/**
 * The answer as a projector should read it.
 *
 * The rail renders the AI's reply through a markdown component; present mode
 * draws it into a plain React Native <Text>, where `**like this**` and a stray
 * `<p>` arrive as literal characters on the wall. Stripping is the honest fix —
 * a projector wants the sentence, not the emphasis — and it is done here rather
 * than in the panel so it can be tested without rendering anything.
 */
export const toPlainAnswer = (value) => {
  if (typeof value !== "string") return "";

  let text = value;

  // Block-level tags become line breaks before every tag is dropped, so a
  // paragraph does not collapse into the sentence before it. A paragraph gets
  // a blank line and a list item a single one, which is the difference between
  // two thoughts and two items on a wall.
  text = text.replace(/<\s*br\s*\/?>/gi, "\n");
  text = text.replace(/<\s*\/\s*li\s*>/gi, "\n");
  text = text.replace(/<\s*\/\s*(p|div|h[1-6])\s*>/gi, "\n\n");
  text = text.replace(/<[^>]*>/g, "");

  text = text.replace(
    /&(amp|lt|gt|quot|#39|apos|nbsp);/gi,
    (match) => HTML_ENTITIES[match.toLowerCase()] || match
  );

  // Markdown emphasis and code fences carry no meaning once the type scale is
  // doing the emphasising.
  text = text.replace(/```[a-z]*\n?/gi, "");
  text = text.replace(/(\*\*|__)(.*?)\1/g, "$2");
  text = text.replace(/`([^`]+)`/g, "$1");
  // Headings become plain lines; a heading inside a five-sentence answer is
  // noise on a slide that already has a title.
  text = text.replace(/^\s{0,3}#{1,6}\s+/gm, "");
  // A list still reads as a list, so the marker is normalised rather than lost.
  text = text.replace(/^\s{0,3}[-*+]\s+/gm, "• ");

  text = text.replace(/\n{3,}/g, "\n\n");
  return text.trim();
};

/** The answer text out of a finished non-streaming payload, in the app's order. */
const answerTextOf = (payload) => {
  if (!payload || typeof payload !== "object") return "";
  return String(payload["AI response"] || payload["Formatted AI response"] || "");
};

/**
 * Ask Saras one question and resolve with the answer as plain text.
 *
 * Resolves with `null` when the request was aborted — a teacher who closed the
 * panel is not a failure, and the caller has nothing left to draw.
 *
 * @param {Object}   opts
 * @param {string}   opts.question   what to ask
 * @param {string}   [opts.userId]   the signed-in teacher, for session threading
 * @param {string}   [opts.language] preferred answer language
 * @param {boolean}  [opts.newDialog] start a fresh thread rather than following on
 * @param {Object}   [opts.journeyContext] Journey hidden/student context when presenting a Journey
 * @param {string|null} [opts.screenshot] Optional visible Journey screenshot data URL, child self-study only
 * @param {AbortSignal} [opts.signal]
 * @param {(partial: string) => void} [opts.onDelta] called as text streams in
 * @param {boolean} [opts.audioEnabled] opt-in streaming TTS (teacher mode remains silent)
 * @param {(url: string) => void} [opts.onAudioChunk] receive streamed audio URLs
 * @returns {Promise<string|null>}
 */
export async function askSaras({
  question,
  userId,
  language = "en",
  newDialog = false,
  journeyContext = null,
  screenshot = null,
  signal,
  onDelta,
  onAudioChunk,
  audioEnabled = false,
}) {
  const clean = String(question || "").trim();
  if (!clean) throw new Error(ASK_EMPTY);
  if (clean.length > PRESENT_ASK_MAX_CHARS) throw new Error(ASK_TOO_LONG);

  // Best effort: an unreadable store must not stop the teacher asking. Without
  // it the backend simply threads this question on its own.
  let clientSessionId = null;
  try {
    clientSessionId = await AsyncStorage.getItem("client_session_id");
  } catch (_e) {
    clientSessionId = null;
  }

  const contentDb = axios?.defaults?.headers?.common?.["X-Content-DB"] || null;

  // Do not pay for / stream audio unless a caller can actually play it.
  // Teacher-led mode stays silent by default.
  const requestAudio = Boolean(audioEnabled && typeof onAudioChunk === "function");
  const body = {
    user_id: userId || "Guest",
    question: clean,
    new_dialog: !!newDialog,
    // Existing Ask AI screenshot transport, now used for normal child Journey
    // questions too. Null in teacher-present mode; optional when capture fails.
    context: screenshot && journeyContext?.self_guided_active
      ? { type: "question", screenshot }
      : { type: "question" },
    // Same field AITutorApp's normal Journey ask path sends. Keeping it
    // separate from `context` preserves hidden context, current student work,
    // and authored reveal policy without changing Lesson Present mode.
    journey_context: journeyContext || null,
    preferred_language: language || "en",
    content_db: contentDb,
    client_session_id: clientSessionId || null,

    // No reference cards: a wall has no room for a citation list, and the
    // teacher cannot click one anyway.
    include_references: false,
    // The room reads this together, out loud, in the middle of a lesson. A
    // three-paragraph answer is a worse answer here even when it is a better
    // one on a laptop.
    answer_length: "short",
    // Curriculum content is the point, but a question from the floor may sit
    // just outside it, and "I can only answer from the textbook" is a dead end
    // in front of thirty people.
    restrict_to_rag_only: false,
    // Reuse AI Tutor's existing streaming TTS inside the same ask_ai request.
    // When speech is disabled, avoid all backend TTS work.
    is_speaker_muted: !requestAudio,
    stream_response: true,
  };

  let live = "";
  let finalText = "";
  // streamNdjson wraps every onEvent call in the same try/catch it uses for
  // malformed lines, so a throw from inside the callback is swallowed as a
  // parse warning. Errors are carried out instead of raised.
  let streamError = null;

  await streamNdjson({
    url: ASK_URL,
    body,
    signal,
    onEvent: (event) => {
      if (!event || typeof event !== "object") return;

      switch (event.type) {
        case "text_delta":
          live += event.delta || "";
          if (onDelta) onDelta(toPlainAnswer(live));
          break;
        case "audio_chunk":
          if (requestAudio && event.url && !signal?.aborted) onAudioChunk(event.url);
          break;
        case "text_done":
          finalText = event.text || live;
          break;
        case "done":
          if (!finalText) finalText = answerTextOf(event) || live;
          break;
        case "error":
          streamError = new Error(event.message || "Ask failed");
          break;
        default:
          // Moderation and other early exits answer with a plain JSON body even
          // though streaming was requested. It arrives here as one untyped
          // event, and it is still this request's real answer.
          if (!event.type && !finalText) finalText = answerTextOf(event);
          break;
      }
    },
  });

  if (streamError) throw streamError;
  // streamNdjson resolves quietly on abort, so an empty result after a cancel
  // is the teacher closing the panel rather than a failed ask.
  if (signal?.aborted) return null;

  const answer = toPlainAnswer(finalText || live);
  if (!answer) throw new Error("Ask returned no answer text");
  return answer;
}

export default askSaras;
